import {
  compareTags,
  tagCategoryInfo,
  type SemanticTag,
  type TagCategory,
  type TagTarget,
  type TagTargetKind,
} from '@/semantics/SemanticTag';
import { formatBytes, totalImageBytes, type TagImage } from '@/semantics/TagImage';
import { formatLength } from './dom';

/**
 * Every semantic tag on the site, gathered into something readable.
 *
 * ## Why a list is worth building at all
 *
 * A tag is invisible by construction — it changes no geometry — so until now the only way to find
 * out what a site had been told was to pick the tag tool up and click every object in turn. That is
 * fine for checking one bench and useless for the question people actually have, which is *what will
 * the render be told, in total, and is any of it wrong by now?* Two tags that contradict each other,
 * a "keep out" left on a bed that has since been planted, three photographs quietly eating the
 * document's picture budget — none of those are findable one click at a time.
 *
 * So the tags get the treatment the planting already has: the site read as a document rather than as
 * a model. This file is deliberately the twin of `PlantManifest.ts`.
 *
 * ## Grouped by subject, not by category
 *
 * The default grouping is what a tag is *about*, broadest first: the whole site, then its ground
 * materials, then places, then built things, then plants. That is the order a reader checks them in,
 * because the whole-site register is the thing that makes every other tag land differently — it
 * belongs at the top, not filed under "style" halfway down an alphabet. Grouping by category is
 * offered as the second reading, because "show me everything I said to keep out" is a real question
 * with a different shape.
 *
 * ## DOM-free on purpose
 *
 * `TagIndexPanel` renders this; nothing here knows the panel exists. Same split as
 * `PlantManifest` / `PlantManifestPanel` and `HistoryView` / `HistoryPanel`, and for the same
 * reason: the derivation is the half worth testing, and it should not need a browser to run.
 */

export interface TagIndexEntry {
  tag: SemanticTag;
  /** Human name of the thing the tag is attached to — "Acer rubrum", "Self-binding gravel". */
  targetName: string;
  targetKind: TagTargetKind;
  /** Attached photographs, in the order they were attached. Empty rather than undefined here. */
  images: readonly TagImage[];
  /** What this tag's pictures cost the document, encoded. */
  imageBytes: number;
  /** Everything searchable about the tag, folded to lower case once at build time. */
  haystack: string;
}

export interface TagIndexSection {
  /** Stable key — the target kind for a subject grouping, the category for a category one. */
  id: string;
  title: string;
  entries: TagIndexEntry[];
}

export interface TagIndex {
  entries: TagIndexEntry[];
  totalTags: number;
  /** Tags carrying at least one picture. */
  taggedWithImages: number;
  totalImages: number;
  imageBytes: number;
}

export type TagGrouping = 'subject' | 'category';

/**
 * Subject order: broadest claim first.
 *
 * Not alphabetical and not insertion order. A whole-site tag changes how every other tag is read, a
 * ground tag changes every surface it names, and a plant tag changes one plant — so the list reads
 * from the widest claim down to the narrowest.
 */
export const TAG_SUBJECT_ORDER: readonly TagTargetKind[] = [
  'site',
  'ground',
  'location',
  'structure',
  'plant',
];

/**
 * Category order for the second grouping: the declaration order in `SemanticTag.ts`, which runs from
 * what a thing *is* through how it looks to what must not be there. A progression, not an alphabet.
 */
export const TAG_CATEGORY_ORDER: readonly TagCategory[] = [
  'identity',
  'material',
  'use',
  'style',
  'condition',
  'avoid',
];

const SUBJECT_TITLES: Record<TagTargetKind, string> = {
  site: 'The whole site',
  ground: 'Ground materials',
  location: 'Places',
  structure: 'Buildings and props',
  plant: 'Plants',
};

export function subjectTitle(kind: TagTargetKind): string {
  return SUBJECT_TITLES[kind];
}

export interface BuildTagIndexOptions {
  tags: readonly SemanticTag[];
  /** Human name for a target. Supplied by the app, the only thing that knows all three worlds. */
  describeTarget: (target: TagTarget) => string;
}

/** Every tag, named, measured, and sorted loudest-first the way the store and the prompt sort them. */
export function buildTagIndex(options: BuildTagIndexOptions): TagIndex {
  const entries = options.tags
    .map((tag) => toEntry(tag, options.describeTarget(tag.target)))
    .sort((a, b) => compareTags(a.tag, b.tag));

  const images = entries.flatMap((entry) => [...entry.images]);
  return {
    entries,
    totalTags: entries.length,
    taggedWithImages: entries.filter((entry) => entry.images.length > 0).length,
    totalImages: images.length,
    imageBytes: totalImageBytes(images),
  };
}

function toEntry(tag: SemanticTag, targetName: string): TagIndexEntry {
  const images = tag.images ?? [];
  return {
    tag,
    targetName,
    targetKind: tag.target.kind,
    images,
    imageBytes: totalImageBytes(images),
    haystack: [
      tag.label,
      tag.note ?? '',
      targetName,
      tagCategoryInfo(tag.category).name,
      tag.category,
      tag.emphasis,
      ...images.map((image) => `${image.caption ?? ''} ${image.name ?? ''}`),
    ]
      .join(' ')
      .toLowerCase(),
  };
}

/**
 * Split a list of entries into sections.
 *
 * Empty sections are dropped rather than shown greyed: a site with no ground tags has nothing to say
 * about ground tags, and a column of zeroes is noise in a list whose whole job is to be skimmable.
 */
export function sectionTags(
  entries: readonly TagIndexEntry[],
  grouping: TagGrouping,
): TagIndexSection[] {
  const sections: TagIndexSection[] = [];
  const keys: readonly string[] = grouping === 'subject' ? TAG_SUBJECT_ORDER : TAG_CATEGORY_ORDER;

  for (const key of keys) {
    const members = entries.filter((entry) =>
      grouping === 'subject' ? entry.targetKind === key : entry.tag.category === key,
    );
    if (members.length === 0) continue;
    sections.push({
      id: key,
      title:
        grouping === 'subject'
          ? subjectTitle(key as TagTargetKind)
          : tagCategoryInfo(key as TagCategory).name,
      entries: members,
    });
  }
  return sections;
}

/** Whether a tag answers a search. Matched against the whole record, its pictures' captions included. */
export function matchesTagQuery(entry: TagIndexEntry, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return entry.haystack.includes(needle);
}

export interface TagFilter {
  query: string;
  /** Narrow to tags carrying at least one picture — the "what am I actually shipping?" question. */
  picturesOnly?: boolean;
  /** Narrow to one category, or every category when absent. */
  category?: TagCategory | null;
}

export function filterTags(entries: readonly TagIndexEntry[], filter: TagFilter): TagIndexEntry[] {
  return entries.filter((entry) => {
    if (filter.picturesOnly === true && entry.images.length === 0) return false;
    if (filter.category != null && entry.tag.category !== filter.category) return false;
    return matchesTagQuery(entry, filter.query);
  });
}

/**
 * The header line: how much the site has been told, and what it costs.
 *
 * The picture weight is in it rather than tucked away because it is the one number here with a hard
 * limit behind it (`TAG_IMAGE_BUDGET.maxSiteBytes`), and a designer who has quietly attached four
 * megabytes of fence photographs should find that out while reading the list rather than when a save
 * starts to drag.
 */
export function summariseTagIndex(index: TagIndex): string {
  if (index.totalTags === 0) return 'Nothing tagged yet';
  const parts = [plural(index.totalTags, 'tag')];
  if (index.totalImages > 0) {
    parts.push(
      `${plural(index.totalImages, 'picture')} on ${plural(index.taggedWithImages, 'tag')}`,
      formatBytes(index.imageBytes),
    );
  }
  return parts.join('  ·  ');
}

/**
 * The precise half of what a tag is attached to.
 *
 * Only location tags have one. "This spot" is the single target that cannot be recognised from its
 * name — two of them read identically in a list until you can see that one covers three metres and
 * the other covers thirty.
 */
export function targetDetail(tag: SemanticTag, unit: 'meters' | 'feet' = 'meters'): string {
  if (tag.target.kind !== 'location') return '';
  const { x, z, radiusMeters } = tag.target;
  return `${formatLength(radiusMeters, unit)} radius at ${round(x)}, ${round(z)}`;
}

/** A tag's creation date, written the way a person would date a note. */
export function formatTagDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * The list as CSV — the same rows, filter and all, that are on screen.
 *
 * Pictures are reported as a count and a weight rather than embedded: a data URL in a spreadsheet
 * cell is not a picture, it is a hundred thousand characters that break the file for every reader.
 */
export function tagsCsv(entries: readonly TagIndexEntry[]): string {
  const lines = ['Subject,Attached to,Category,Emphasis,Label,Note,Pictures,Picture size,Added'];
  for (const entry of entries) {
    lines.push(
      [
        subjectTitle(entry.targetKind),
        quote(entry.targetName),
        tagCategoryInfo(entry.tag.category).name,
        entry.tag.emphasis,
        quote(entry.tag.label),
        quote(entry.tag.note ?? ''),
        String(entry.images.length),
        entry.images.length > 0 ? formatBytes(entry.imageBytes) : '',
        entry.tag.createdAt,
      ].join(','),
    );
  }
  return `${lines.join('\n')}\n`;
}

/* -------------------------------------- Helpers -------------------------------------- */

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function quote(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function round(value: number): string {
  return Math.abs(value) < 10 ? value.toFixed(1) : String(Math.round(value));
}
